import React, { useState, useEffect, useContext } from 'react';
import { Button } from 'primereact/button';
import { listExpenses, deleteExpense } from 'services/billingServices';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import useSpinner from 'hooks/useSpinner';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { dollarFormat, getTenantDetails, paginationDropdownOptions } from 'utils/utils';
import ImageComponent from 'shared/ImageComponent';
import { MultiSelect } from 'primereact/multiselect';
import { useDispatch, useSelector } from 'react-redux';
import { getPracticeAreaAction } from 'redux/actions/PracticeAreaAction';
import { totalCountEvent } from 'services/generalServices';
import { Tooltip } from 'primereact/tooltip';
import { handleDateTimeOffset } from 'utils/utility_functions/timezone';
import constants from 'constants/index';
import { UserDetailsContext } from 'context/userDetailsContext';
import { CreateExpense } from './CreateExpense/CreateExpense';
import { handlePageNumberInPagination } from 'common/pagination';
import { expenseStatusOptions } from 'constants/dropdownOptions';
import moment from 'moment';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import GlobalTooltip from 'components/GlobalTooltip';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import { setTimezoneWithoutAlteringTime } from 'utils/utility_functions/timezone';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';

const Expenses = (props) => {
  const { isLeadSpecific, isFromCaseSpecificModule, handleExpenseTableRedirection, getTimeEntrySummarty, isFromGlobalBilling } = props;
  const userContext = useContext(UserDetailsContext);
  const [modal, setModal] = useState(false);
  const [expenseList, setExpenseList] = useState([]);
  const [selectedExpense, setSelectedExpense] = useState();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { addToast } = useToast();
  const dispatch = useDispatch();
  const [spinner, showSpinner, hideSpinner] = useSpinner(true);
  const [searchKey, setSearchKey] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedStatus, setSelectedStatus] = useState();
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [pageLimit, setPageLimit] = useState(getPageLimit() ?? 10);
  const [isForward, setIsForward] = useState(true);
  const practiceData = useSelector((state) => state.practiceArea.practiceAreaList['practice-area']);
  const [practiceAreaOptions, setPracticeAreaOptions] = useState([]);
  const next_Token = expenseList.next_token;
  const transition_Token = expenseList.transition_token;
  const [tenantId] = getTenantDetails();

  function getPageLimit() {
    if (isLeadSpecific && userContext) {
      return userContext?.firmDetails?.pagination_preference?.leads?.billings?.expenses;
    } else if (isFromCaseSpecificModule && userContext) {
      return userContext?.firmDetails?.pagination_preference?.case?.billings?.expenses;
    } else if (userContext) {
      return userContext?.firmDetails?.pagination_preference?.billings?.expenses;
    }
  }

  function getFilePathForPagination() {
    if (isLeadSpecific && userContext) {
      return { module: 'leads', subModule: 'billings', listName: 'expenses' };
    } else if (isFromCaseSpecificModule && userContext) {
      return { module: 'case', subModule: 'billings', listName: 'expenses' };
    } else {
      return { module: 'billings', subModule: '', listName: 'expenses' };
    }
  }

  const loadExpenseData = (filter, pageLimit, pagination_token, pagination_direction) => {
    showSpinner();
    if (props.caseView) {
      filter.case_id = props?.caseDetails?.case_id;
    }
    listExpenses(filter, pageLimit, pagination_token, pagination_direction)
      .then((response) => {
        if (response?.data) {
          let expenses = response.data;
          setExpenseList(expenses);
          hideSpinner();
        }
      })
      .catch((err) => {
        hideSpinner();
      });
  };

  const caseBody = (rowData) => {
    const { case_id, is_lead } = rowData;
    const mainModuleName = is_lead ? '/leads' : '/cases';
    let route = case_id ? `${mainModuleName}/${case_id}` : mainModuleName;

    return (
      <React.Fragment>
        <span className="p-column-title text-break">Case</span>
        <Link to={route} className="redirection-link-no-text-transform" title={rowData.case_name ? rowData.case_name : undefined}>
          {rowData.case_name ? rowData.case_name : '-'}
        </Link>
      </React.Fragment>
    );
  };
  const typeBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Type</span>
        <span className="text-break" title={rowData.expense_type}>
          {rowData.expense_type}
        </span>
      </React.Fragment>
    );
  };

  const teamMemberBody = (rowData) => {
    const firmUserId = rowData?.team_member_id || rowData?.firm_user_id;
    return (
      <Link
        className="d-inline-flex redirection-link-no-text-transform"
        to={firmUserId ? `/settings/team-management/${firmUserId}` : '/settings/team-management'}
      >
        <span className="p-column-title text-break">Team Member</span>
        <span className="d-flex">
          <ImageComponent filePath={`${tenantId}/${firmUserId}/profile_image`} fileName={rowData?.team_member_name} fileSize="small.jpg" />
          <span
            className="redirection-link-no-text-transform"
            title={rowData?.first_name}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '180px',
            }}
          >
            {firmUserId && <i className="fas fa-user-tie me-2"></i>}
            {rowData?.first_name ? `${rowData?.first_name} ${rowData?.last_name}` : '-'}
          </span>
        </span>
      </Link>
    );
  };

  const statusBody = (rowData) => {
    let status = '';
    if (rowData?.expense_status === 'UNINVOICED') {
      status = 'Uninvoiced';
    } else {
      status = 'Invoiced';
    }
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Status</span>
        <span className="text-break" title={status}>
          {status}
        </span>
      </React.Fragment>
    );
  };

  const totalAmountBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Total Amount</span>
        <span className="text-break" title={dollarFormat(rowData.total_amount)}>
          {dollarFormat(rowData.total_amount)}
        </span>
      </React.Fragment>
    );
  };

  const dateBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Date</span>
        {rowData.expense_date ? (
          <span
            className="text-break"
            title={handleDateTimeOffset(userContext?.userDetails?.timezone, rowData.expense_date, constants.month_date_year_format)}
          >
            {handleDateTimeOffset(userContext?.userDetails?.timezone, rowData.expense_date, constants.month_date_year_format)}
          </span>
        ) : (
          'N/A'
        )}
      </React.Fragment>
    );
  };
  const actionBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Action</span>
        <span role="button">
          <GlobalTooltip tooltip="Edit" className="ms-2">
            <i
              className="icon-edit cursor-pointer"
              onClick={() => {
                setSelectedExpense({ ...rowData });
                setModal(true);
              }}
              id="edit"
            />
          </GlobalTooltip>
        </span>
        <span role="button">
          <GlobalTooltip tooltip="Delete" className="ms-4 ">
            <i
              className="icon-delete cursor-pointer"
              onClick={() => {
                setSelectedExpense({ ...rowData });
                setConfirmDelete(true);
              }}
              id="delete"
            />
          </GlobalTooltip>
        </span>
      </React.Fragment>
    );
  };

  const onCreateSuccess = () => {
    setModal(false);
    loadExpenseData(
      {
        case_id: '',
        expense_status: selectedStatus ? selectedStatus : '',
        expense_date_lower: '',
        expense_date_upper: '',
        expense_filter: 0,
        search_key: searchKey,
        practice_area: selectedPractice,
      },
      pageLimit,
      '',
      'forward'
    );
    typeof getTimeEntrySummarty === 'function' && getTimeEntrySummarty();
  };

  const pickExtractedDate = (dateArray) => {
    return {
      expense_date_lower: dateArray?.[0]
        ? setTimezoneWithoutAlteringTime(moment(dateArray[0]).startOf('day'), userContext?.userDetails.timezone)
        : '',
      expense_date_upper: dateArray?.[0]
        ? setTimezoneWithoutAlteringTime(moment(dateArray[1] ? dateArray[1] : dateArray[0]).endOf('day'), userContext?.userDetails.timezone)
        : '',
    };
  };

  const onDeleteExpense = () => {
    let sk = selectedExpense?.sk;
    sk = sk ? sk.replaceAll('#', '%23') : '';
    deleteExpense(sk, props.isLeadSpecific ? props.isLeadSpecific : selectedExpense?.is_lead)
      .then((response) => {
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.EXPENSE_DELETE_SUCCESS);
        loadExpenseData(
          {
            case_id: '',
            expense_status: selectedStatus ? selectedStatus : '',
            ...pickExtractedDate(selectedDate),
            expense_filter: 0,
            search_key: searchKey,
            practice_area: selectedPractice,
          },
          pageLimit,
          '',
          'forward'
        );
        typeof getTimeEntrySummarty === 'function' && getTimeEntrySummarty();
      })
      .catch((err) => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.EXPENSE_DELETE_FAILURE);
      });
  };

  const onChangeCalendar = (e) => {
    setSelectedDate(e.value);
    loadExpenseData(
      {
        case_id: '',
        expense_status: selectedStatus ? selectedStatus : '',
        ...pickExtractedDate(e.value),
        expense_filter: 0,
        search_key: searchKey,
        practice_area: selectedPractice,
      },
      pageLimit,
      '',
      'forward'
    );
  };

  const onSearchKeyDown = (e) => {
    let code = e.keyCode ? e.keyCode : e.which;
    if (code === 13) {
      loadExpenseData(
        {
          case_id: '',
          expense_status: selectedStatus ? selectedStatus : '',
          ...pickExtractedDate(selectedDate),
          expense_filter: 0,
          search_key: searchKey,
          practice_area: selectedPractice,
        },
        pageLimit,
        '',
        'forward'
      );
    }
  };

  const onChangeSearch = (e) => {
    let value = e.target.value;
    setSearchKey(value);
    if (!value) {
      loadExpenseData(
        {
          case_id: '',
          expense_status: selectedStatus ? selectedStatus : '',
          ...pickExtractedDate(selectedDate),
          expense_filter: 0,
          search_key: '',
          practice_area: selectedPractice,
        },
        pageLimit,
        '',
        'forward'
      );
    }
  };

  const onChangeStatus = (e) => {
    setSelectedStatus(e.value);
    loadExpenseData(
      {
        case_id: '',
        expense_status: e.value ? e.value : '',
        ...pickExtractedDate(selectedDate),
        expense_filter: 0,
        search_key: searchKey,
        practice_area: selectedPractice,
      },
      pageLimit,
      '',
      'forward'
    );
  };
  const onChangePractice = (e) => {
    setSelectedPractice([...e.value]);
    loadExpenseData(
      {
        case_id: '',
        expense_status: selectedStatus,
        ...pickExtractedDate(selectedDate),
        expense_filter: 0,
        practice_area: [...e.value],
        search_key: searchKey,
      },
      pageLimit,
      '',
      'forward'
    );
  };
  const refreshCase = () => {
    setSelectedStatus();
    setSearchKey('');
    setSelectedDate('');
    setSelectedPractice('');
    loadExpenseData(
      {
        case_id: '',
        expense_status: '',
        expense_date_lower: '',
        expense_date_upper: '',
        expense_date: '',
        expense_filter: 0,
        search_key: '',
        practice_area: '',
      },
      10,
      '',
      'forward'
    );
  };

  const paginatorTemplate = {
    layout: 'RowsPerPageDropdown PrevPageLink NextPageLink',
    RowsPerPageDropdown: (options) => {
      return (
        <React.Fragment>
          <span className="mx-1" style={{ color: 'var(--black-500)', userSelect: 'none' }}>
            Item Per Page
          </span>
          <Dropdown value={options.value} options={paginationDropdownOptions} onChange={onPageChange} />
        </React.Fragment>
      );
    },
    NextPageLink: ({ iconClassName }) => {
      return (
        <Button
          onClick={onNextPageClick}
          disabled={next_Token === null && isForward}
          className={'p-paginator-next p-paginator-element p-link'}
          icon={iconClassName}
        />
      );
    },
    PrevPageLink: ({ iconClassName }) => {
      return (
        <Button
          onClick={onPrevPageClick}
          disabled={transition_Token === null || (transition_Token && next_Token === null && !isForward)}
          className={'p-paginator-next p-paginator-element p-link'}
          icon={iconClassName}
        />
      );
    },
  };
  const onNextPageClick = () => {
    loadExpenseData(
      {
        case_id: '',
        expense_status: selectedStatus || '',
        ...pickExtractedDate(selectedDate),
        expense_filter: 0,
        search_key: '',
        practice_area: selectedPractice || '',
      },
      pageLimit,
      !isForward ? transition_Token : next_Token,
      'forward'
    );
    setIsForward(true);
  };
  const onPrevPageClick = () => {
    loadExpenseData(
      {
        case_id: '',
        expense_status: selectedStatus || '',
        ...pickExtractedDate(selectedDate),
        expense_filter: 0,
        search_key: '',
        practice_area: selectedPractice || '',
      },
      pageLimit,
      isForward ? transition_Token : next_Token,
      'reverse'
    );
    setIsForward(false);
  };
  const onPageChange = (event) => {
    setPageLimit(event.value);
    let path = getFilePathForPagination();
    handlePageNumberInPagination(userContext, path?.module, path?.subModule, path?.listName, event.value);
    loadExpenseData(
      {
        case_id: '',
        expense_status: selectedStatus || '',
        ...pickExtractedDate(selectedDate),
        expense_filter: 0,
        search_key: '',
        practice_area: selectedPractice || '',
      },
      event.value,
      '',
      'forward'
    );
  };

  useEffect(() => {
    loadExpenseData(
      {
        expense_status: selectedStatus ? selectedStatus : '',
        ...pickExtractedDate(selectedDate),
        search_key: searchKey,
        practice_area: selectedPractice,
      },
      pageLimit,
      '',
      'forward'
    );
    totalCountEvent('practice-area')
      .then((response) => {
        dispatch(getPracticeAreaAction(response.data, '', 'forward', true));
      })
      .catch((err) => {});
  }, []);

  useEffect(() => {
    if (practiceData && practiceData.length) {
      let practice_options = practiceData.map((val) => {
        return {
          label: val.practice_area_name,
          value: val.practice_area_id,
        };
      });
      setPracticeAreaOptions(practice_options);
    } else {
      setPracticeAreaOptions([]);
    }
  }, [practiceData]);

  return (
    <div>
      {spinner}
      <ConfirmDialog
        className="confirm-delete-popup"
        visible={confirmDelete}
        onHide={() => setConfirmDelete(false)}
        header="Confirm Delete"
        rejectClassName="p-button-secondary outline"
        message={' Do you want to delete the expense data?'}
        icon="pi pi-exclamation-triangle"
        accept={() => {
          onDeleteExpense();
        }}
        reject={() => setConfirmDelete(false)}
      />
      {modal && (
        <CreateExpense
          modal={modal}
          setModal={setModal}
          onCreateSuccess={onCreateSuccess}
          selectedExpense={selectedExpense}
          caseView={props.caseView}
          caseDetails={props.caseDetails}
          isLeadSpecific={props.isLeadSpecific}
        />
      )}
      <div className="container-fluid mx-0 p-0">
        <div className="">
          <div className="col-12">
            <div className="d-flex  align-items-center mb-3">
              <Button
                label="Add Expense"
                icon="pi pi-plus"
                className="p-button p-button-primary ms-auto"
                onClick={() => {
                  setSelectedExpense();
                  setModal(true);
                }}
              ></Button>
            </div>
          </div>
        </div>
        <div className="">
          <div className="d-flex flex-wrap align-items-center filter-wrap mb-3 p-lg-0 p-2 w-100 align-items-center justify-content-between">
            <div className="p-2 filter-wraper">
              <span className="p-input-icon-left input-shadow">
                <i className="icon-search F-size16"></i>
                <TextSnippetWrapper
                  type="text"
                  placeholder="Search"
                  onChange={(e) => {
                    onChangeSearch(e);
                  }}
                  value={searchKey}
                  onKeyDown={onSearchKeyDown}
                  className="p-inputtext p-component input-search"
                  autoComplete="off"
                />
              </span>
            </div>
            <div className="d-flex flex-wrap align-items-center p-2 expense-filter filter-wraper">
              <label className="text-bold black-600 filter-label">Filters:</label>
              <div className="d-flex flex-column mx-lg-2 mx-0 filter-wraper">
                <Calendar
                  selectionMode="range"
                  className="input-shadow filter-calender"
                  id="icon"
                  value={selectedDate}
                  onChange={(e) => {
                    onChangeCalendar(e);
                  }}
                  showIcon
                  placeholder="Expense Date"
                />
              </div>
              <div className="d-flex flex-column me-2 filter-wraper">
                <Dropdown
                  options={expenseStatusOptions}
                  onChange={onChangeStatus}
                  value={selectedStatus}
                  placeholder="Invoice Status"
                  className="searchable-dropdown"
                />
              </div>
              {!isFromCaseSpecificModule && !isLeadSpecific && (
                <div className="d-flex flex-column me-2 filter-wraper">
                  <MultiSelect
                    value={selectedPractice}
                    options={practiceAreaOptions}
                    onChange={onChangePractice}
                    placeholder="Practice Area"
                    maxSelectedLabels={1}
                    display="chip"
                    filter
                    className="input-shadow w-100 F-size14 searchable-dropdown"
                  />
                </div>
              )}

              <div className="d-flex flex-column mx-2 filter-wraper text-center">
                <div className="pointer ms-0">
                  <i className="fas fa-sync-alt" onClick={refreshCase}></i>
                  <Tooltip content="Reset Filter" position="top" target=".fa-sync-alt" showDelay={500} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 p-0 datatable-responsive">
          <ResizableDataTable
            value={expenseList?.expenses || []}
            className="p-datatable-responsive"
            paginator
            paginatorTemplate={paginatorTemplate}
            rows={pageLimit}
            storageKey="expenses-list"
            columns={[
              {
                field: 'case_id',
                header: 'Case/Lead',
                body: caseBody,
                clickable: true,
                clickableKey: 'case_id',
                bodyClassName: 'ellipsis-text',
                hidden: !isFromGlobalBilling,
              },
              { field: 'expense_type', header: 'Type', body: typeBody, bodyClassName: 'ellipsis-text' },
              { field: 'team_member_name', header: 'Team Member', body: teamMemberBody, bodyClassName: 'ellipsis-text' },
              { field: 'expense_status', header: 'Status', body: statusBody, bodyClassName: 'ellipsis-text' },
              { field: 'total_amount', header: 'Total Amount', body: totalAmountBody, bodyClassName: 'ellipsis-text' },
              { field: 'expense_date', header: 'Date', body: dateBody, bodyClassName: 'ellipsis-text' },
              { field: 'action', bodyClassName: 'action-column', body: actionBody },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default Expenses;
